import React, { Fragment, useEffect, useState, useRef } from "react";
import { Input, Flex } from "antd";

import Container from "@/components/container/Container";
import CardComponent from "@/components/card";
import BackWithLabel from "@/components/common/BackWithLabel";

import IconBackArrow from "@/components/icons/IconBackArrow";
import IconUserProfile from "@/components/icons/IconUserProfile";
import IconSent from "@/components/icons/IconSent";

import "@/styles/dashboard/conversations.scss";
import { getChatMessages, markChatAsRead } from "@/services/users/chatMessages";

import { getUserInfo } from "@/reducers/commonSlice";
import { useSelector } from "react-redux";
import { getChatMembers } from "@/services/users/chatMembers";
import { getStudentConversations } from "@/services/students/getStudentConversations";
import { getGuideConversations } from "@/services/guides/getGuideConversations";
import ButtonComponent from "@/components/button/Button";

import socket from "@/socket";

const Conversations = ({ isGuidePanel = false }) => {
  const [selectedTab, setSelectedTab] = useState(null);
  const [chatLables, setChatLabels] = useState([]);
  const [chat, setChat] = useState([]);
  const [message, setMessage] = useState("");
  const [groupId, setGroupId] = useState(""); // Group/Product ID
  const [chatMembers, setChatMembers] = useState([]);
  const [markAsRead, setMarkAsRead] = useState([]);

  const userInfo = useSelector(getUserInfo);
  const conversationRef = useRef(null);
  const token = localStorage.getItem("token");

  const selectTab = async (tab, index) => {
    tab["index"] = index;
    setSelectedTab(tab);
    if (tab?.metadata && Object.keys(tab?.metadata).length > 0) {
      setGroupId(tab?.metadata?.groupChatId);
      await fetchMessages(tab?.metadata?.groupChatId);

      // Mark chat as read if it's unread
      if (!tab?.metadata?.isAllChatsRead) {
        try {
          await markChatAsRead(tab?.metadata?.groupChatId);
          // Update the chat's read status in the state
          setChatLabels((prev) =>
            prev.map((chat) => {
              if (chat.metadata?.groupChatId === tab.metadata?.groupChatId) {
                return {
                  ...chat,
                  metadata: {
                    ...chat.metadata,
                    isAllChatsRead: true,
                  },
                };
              }
              return chat;
            })
          );
        } catch (error) {
          console.error("Error marking chat as read:", error);
        }
      }
    } else {
      setChat([]);
    }
  };

  const scrollToBottom = () => {
    if (conversationRef.current) {
      conversationRef.current.scrollTop = conversationRef.current.scrollHeight;
    }
  };

  const fetchChatMembers = async (groupId) => {
    const chatMembersResponse = await getChatMembers(groupId);
    const chatMembersData = chatMembersResponse?.members || [];
    setChatMembers(chatMembersData);
    return chatMembersData;
  };

  const fetchMessages = async (groupID) => {
    if (groupID) {
      try {
        // Fetch chat messages after chat members
        const chatMembersData = await fetchChatMembers(groupID);
        const chatMessagesResponse = await getChatMessages(groupID);
        const chatMessages = chatMessagesResponse?.messages || [];
        // Merge chat messages with member details
        const mergedMessages = chatMessages.map((message) => {
          const formatedMessage = matchUserProfile(message, chatMembersData);
          return formatedMessage;
        });
        // Set the merged chat data
        setChat(mergedMessages);
        setTimeout(() => {
          scrollToBottom();
        }, 500);
      } catch (error) {
        setChat([]);
        console.error("Error fetching data:", error);
      }
    }
  };

  const matchUserProfile = (message, chatMembers) => {
    const matchProfile = chatMembers?.find((m) => m.id === message.senderId);
    return {
      ...message,
      name: matchProfile?.name,
      profileImgUrl: matchProfile?.profileImgUrl || "/assets/images/defaultProfile.jpeg",
    };
  };

  const goBack = () => {
    setSelectedTab(null);
  };

  const fetchData = async () => {
    if (isGuidePanel) {
      getGuideConversations()
        .then((res) => {
          const data = [];
          Object.values(res).map((x) => {
            x.purchases.map((y) => {
              data.push({
                ...y,
                productName: x.name + " - " + y.productName,
                image: x.profileImgUrl,
              });
            });
          });
          // Sort chats with unread ones at the top
          const sortedData = data.sort((a, b) => {
            if (a.metadata?.isAllChatsRead === b.metadata?.isAllChatsRead) return 0;
            if (!a.metadata?.isAllChatsRead) return -1;
            return 1;
          });
          setChatLabels(sortedData);
          setSelectedTab(sortedData[0]);
          setGroupId(sortedData[0]?.metadata?.groupChatId);
        })
        .catch((error) => console.log(error));
    } else {
      getStudentConversations()
        .then((res) => {
          if (res) {
            const data = Object.values(res)?.map((d, index) => {
              return { ...d, index };
            });
            // Sort chats with unread ones at the top
            const sortedData = data.sort((a, b) => {
              if (a.metadata?.isAllChatsRead === b.metadata?.isAllChatsRead) return 0;
              if (!a.metadata?.isAllChatsRead) return -1;
              return 1;
            });
            setChatLabels(sortedData);
            setSelectedTab(sortedData[0]);
            setGroupId(sortedData[0]?.metadata?.groupChatId);
          }
        })
        .catch((error) => {
          console.log(error);
        });
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    const messageData = {
      token,
      groupId, // Array of receiver emails
      body: { text: message },
    };

    try {
      // Emit the message to the receiver via socket
      socket.emit("sendGroupMessage", messageData);
      // Update local state
      setChat((prevChat) => [
        ...prevChat,
        {
          senderId: userInfo?.user?.email,
          body: messageData.body,
          name: userInfo?.user?.firstName + userInfo?.user?.lastName,
          profileImgUrl: userInfo?.profileImgUrl || "/assets/images/defaultProfile.jpeg",
        },
      ]);
      setMessage("");
    } catch (error) {
      console.error("Error sending message:", error);
    }
    setTimeout(() => {
      scrollToBottom();
    }, 500);
  };

  useEffect(() => {
    if (groupId) {
      // Register the user with the backend after login
      socket.emit("joinGroup", { token, groupId });

      // Listen for incoming group messages
      socket.on("receiveGroupMessage", async (data) => {
        const chatMembers = await fetchChatMembers(groupId);
        const updated_message = matchUserProfile(data, chatMembers);
        setChat((prevChat) => [...prevChat, updated_message]);
        setTimeout(() => {
          scrollToBottom();
        }, 500);
      });

      return () => {
        socket.off("receiveGroupMessage");
      };
    }
  }, [groupId]);

  useEffect(() => {
    if (groupId) {
      fetchChatMembers(groupId);
      fetchMessages(groupId);
    }
  }, [groupId]);

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <Fragment>
      <div
        className={selectedTab ? "conversation-wrapper student-selected" : "conversation-wrapper"}
      >
        <Container>
          <CardComponent>
            <div
              className="full-layout"
              style={{
                "--my-student-cols": chatLables.length === 0 ? "100%" : "35% 65%",
              }}
            >
              <div className="layout-sidebar">
                <div className="back-button">
                  <BackWithLabel label="Conversations" path="/dashboard" />
                </div>
                <div className="product-wrapper">
                  {chatLables.length ? (
                    <>
                      {chatLables?.map((chat_item, index) => {
                        return (
                          <div
                            key={index}
                            data-id={chat_item?.metadata?.groupChatId}
                            className={
                              selectedTab?.index === index
                                ? `conversation-menu active ${
                                    chat_item?.metadata?.isAllChatsRead ? "" : "unread"
                                  }`
                                : `conversation-menu ${
                                    chat_item?.metadata?.isAllChatsRead ? "" : "unread"
                                  }`
                            }
                            onClick={() => selectTab(chat_item, index)}
                          >
                            <Flex justify="space-between">
                              <Flex align="center" gap={8}>
                                <div className="profile-picture">
                                  {chat_item.image ? (
                                    <div className="icon">
                                      <img
                                        src={chat_item.image}
                                        className="student-img"
                                        width={"100%"}
                                        height={"100%"}
                                      />
                                    </div>
                                  ) : (
                                    <div className="icon lor-icon">
                                      <IconUserProfile />
                                    </div>
                                  )}
                                </div>
                                <div className="conversation-menu-content">
                                  <div className="menu-content-top">
                                    <div className="menu-name body-b1">{chat_item.productName}</div>
                                  </div>
                                </div>
                              </Flex>
                            </Flex>
                          </div>
                        );
                      })}
                    </>
                  ) : (
                    <div className="conversation no-conversation" style={{ height: "100%" }}>
                      <div
                        className="session-outer-card"
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center",
                          margin: "auto 0",
                        }}
                      >
                        <img src="/assets/images/availbility.png" />
                        <div className="title-h4">No Conversations Available</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {chatLables.length ? (
                <div className="content">
                  {selectedTab ? (
                    <div className="chat">
                      <div className="header">
                        <div className="student-info">
                          <div className="content-back-button">
                            <IconBackArrow onClick={goBack} />
                          </div>
                          <div className="profile-picture">
                            {selectedTab.image ? (
                              <div className="icon">
                                <img
                                  src={selectedTab.image}
                                  className="student-img"
                                  width={"100%"}
                                  height={"100%"}
                                />
                              </div>
                            ) : (
                              <div className="icon lor-icon">
                                <IconUserProfile />
                              </div>
                            )}
                          </div>
                          <div className="body-b1">{selectedTab?.productName}</div>
                        </div>
                      </div>
                      <div className="conversation-content" ref={conversationRef}>
                        {chat?.map((msg, index) => (
                          <Flex justify="space-between" key={index}>
                            <Flex align="center" gap={8} style={{ paddingBottom: "10px" }}>
                              <div className="profile-picture">
                                <div className="icon">
                                  <img src={msg?.profileImgUrl} alt="profile" />
                                </div>
                              </div>
                              <div
                                key={index}
                                className={
                                  msg.name === userInfo?.user?.email
                                    ? "my-message"
                                    : "other-message"
                                }
                              >
                                <div className="username">@{msg?.name}</div>
                                {msg.body.text}
                              </div>
                            </Flex>
                          </Flex>
                        ))}
                      </div>
                      <form onSubmit={sendMessage} className="message-form">
                        <div className="conversation-input">
                          <div className="input-container" style={{ overflow: "hidden" }}>
                            <div className="emoji-icon">
                              {/* <IconEmoji width={30} height={30} color="#000" /> */}
                            </div>

                            <div className="input" style={{}}>
                              <Input
                                name="message"
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                placeholder="Type your message here..."
                                style={{
                                  height: 56,
                                  resize: "none",
                                  width: "100%",
                                }}
                              />
                            </div>
                          </div>

                          <div className="send-button">
                            <ButtonComponent
                              shape="circle"
                              varient="primary"
                              disabled={!message.length}
                              icon={<IconSent width={30} height={30} color="#fdfefe" />}
                              htmlType="submit"
                            />
                            {/* <button className="send-button-box" type="submit" dis>
                            <IconSent width={30} height={30} color="#fdfefe" />
                          </button> */}
                          </div>
                        </div>
                      </form>
                    </div>
                  ) : (
                    <Fragment>
                      {/* Any other content you want to show when no student is selected */}
                    </Fragment>
                  )}
                </div>
              ) : null}
            </div>
          </CardComponent>
        </Container>
      </div>
    </Fragment>
  );
};

export default Conversations;
